import { useState, useMemo } from "react";
import type { CountingChallengeContent, DisciplineSlug } from "@jumpbean/shared";

export interface CountingChallengeProps {
  /** Challenge content configuration */
  content: CountingChallengeContent;
  /** Discipline for theming */
  discipline?: DisciplineSlug;
  /** Called when an answer is selected */
  onAnswer: (answer: number) => void;
  /** Currently selected answer */
  selectedAnswer: number | null;
  /** Whether answer has been submitted */
  isSubmitted?: boolean;
  /** Whether the submitted answer was correct */
  isCorrect?: boolean;
}

/**
 * Counting Challenge Component
 * Displays objects to count and number options to select from.
 * Child-friendly with large touch targets and visual feedback.
 */
export function CountingChallenge({
  content,
  discipline = "math",
  onAnswer,
  selectedAnswer,
  isSubmitted = false,
  isCorrect = false,
}: CountingChallengeProps) {
  const [countedItems, setCountedItems] = useState<Set<number>>(new Set());

  // Calculate total objects to count
  const totalObjects = useMemo(() => {
    return content.objects.reduce((sum, obj) => sum + obj.count, 0);
  }, [content.objects]);

  // Generate object items for display
  const objectItems = useMemo(() => {
    const items: Array<{ id: number; type: string; imageUrl?: string }> = [];
    let id = 0;
    for (const obj of content.objects) {
      for (let i = 0; i < obj.count; i++) {
        items.push({ id: id++, type: obj.type, imageUrl: obj.imageUrl });
      }
    }
    return items;
  }, [content.objects]);

  // Handle tapping an object to "count" it
  const handleObjectTap = (id: number) => {
    if (isSubmitted) return;
    setCountedItems((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  // Get emoji for object type
  const getObjectEmoji = (type: string): string => {
    const emojiMap: Record<string, string> = {
      star: "⭐",
      gem: "💎",
      apple: "🍎",
      banana: "🍌",
      heart: "❤️",
      flower: "🌸",
      moon: "🌙",
      ball: "🔵",
      balloon: "🎈",
      cookie: "🍪",
      candy: "🍬",
      butterfly: "🦋",
      fish: "🐟",
      bird: "🐦",
      bunny: "🐰",
      unicorn: "🦄",
      default: "✨",
    };
    return emojiMap[type.toLowerCase()] ?? "✨";
  };

  // Pluralize object type for display
  const pluralize = (type: string): string => {
    const irregulars: Record<string, string> = {
      butterfly: "butterflies",
      bunny: "bunnies",
      candy: "candies",
      fish: "fish",
    };
    const lower = type.toLowerCase();
    if (irregulars[lower]) return irregulars[lower];
    return lower + "s";
  };

  return (
    <div className="flex flex-col items-center gap-8">
      {/* Objects to Count */}
      <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-neutral-200 w-full">
        <div className="flex flex-wrap justify-center gap-4">
          {objectItems.map((item) => (
            <button
              key={item.id}
              type="button"
              onClick={() => handleObjectTap(item.id)}
              disabled={isSubmitted}
              className={`
                w-16 h-16 md:w-20 md:h-20
                flex items-center justify-center
                rounded-xl
                text-4xl md:text-5xl
                transition-all duration-200
                ${
                  countedItems.has(item.id)
                    ? "bg-sunshine-200 scale-110 shadow-md ring-4 ring-sunshine-400"
                    : "bg-neutral-100 hover:bg-neutral-200"
                }
                ${isSubmitted ? "cursor-default" : "cursor-pointer active:scale-95"}
              `}
              aria-label={`${item.type} ${countedItems.has(item.id) ? "counted" : "not counted"}`}
            >
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt={item.type}
                  className="w-12 h-12 md:w-16 md:h-16 object-contain"
                />
              ) : (
                getObjectEmoji(item.type)
              )}
              {countedItems.has(item.id) && (
                <span className="absolute -top-2 -right-2 w-6 h-6 bg-sunshine-500 rounded-full flex items-center justify-center text-xs font-bold text-neutral-900">
                  {Array.from(countedItems)
                    .sort()
                    .indexOf(item.id) + 1}
                </span>
              )}
            </button>
          ))}
        </div>

      </div>

      {/* Answer Options */}
      <div className="w-full">
        <h3 className="text-center font-display font-bold text-body-lg text-white mb-4">
          How many {pluralize(content.objects[0]?.type || "object")} are there?
        </h3>
        <div className="flex flex-wrap justify-center gap-4">
          {content.options.map((option) => (
            <button
              key={option}
              type="button"
              onClick={() => !isSubmitted && onAnswer(option)}
              disabled={isSubmitted}
              className={`
                w-[120px] min-h-[80px] rounded-2xl
                font-display font-bold text-display-md
                border-4 transition-all duration-200
                ${getOptionStyles(option, selectedAnswer, isSubmitted, isCorrect, content.correctAnswer, discipline)}
              `}
              aria-label={`Select ${option}`}
              aria-pressed={selectedAnswer === option}
            >
              {option}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

/**
 * Get styles for answer option based on state
 */
function getOptionStyles(
  option: number,
  selectedAnswer: number | null,
  isSubmitted: boolean,
  isCorrect: boolean,
  correctAnswer: number,
  discipline: DisciplineSlug
): string {
  const isSelected = selectedAnswer === option;
  const isCorrectOption = option === correctAnswer;

  if (isSubmitted) {
    if (isCorrectOption) {
      return `
        bg-meadow-400 border-meadow-600 text-neutral-900
        shadow-[0_4px_0_0_theme(colors.meadow.700)]
        animate-celebrate
      `;
    }
    if (isSelected && !isCorrect) {
      return `
        bg-neutral-200 border-neutral-400 text-neutral-600
        opacity-75
      `;
    }
    return `
      bg-neutral-100 border-neutral-300 text-neutral-500
      opacity-50
    `;
  }

  if (isSelected) {
    return getDisciplineSelectedStyles(discipline);
  }

  return `
    bg-white border-neutral-300 text-neutral-700
    hover:border-neutral-400 hover:bg-neutral-50
    active:scale-95
    shadow-[0_4px_0_0_theme(colors.neutral.300)]
    hover:shadow-[0_2px_0_0_theme(colors.neutral.300)]
    hover:-translate-y-0.5
  `;
}

/**
 * Get selected styles based on discipline
 */
function getDisciplineSelectedStyles(discipline: DisciplineSlug): string {
  const styles: Record<DisciplineSlug, string> = {
    math: `
      bg-luna-light border-luna-dark text-neutral-900
      shadow-[0_4px_0_0_theme(colors.luna.dark)]
      -translate-y-1
    `,
    spelling: `
      bg-petal-light border-petal-dark text-neutral-900
      shadow-[0_4px_0_0_theme(colors.petal.dark)]
      -translate-y-1
    `,
    patterns: `
      bg-prism-yellow border-prism-red text-neutral-900
      shadow-[0_4px_0_0_theme(colors.prism.red.dark)]
      -translate-y-1
    `,
    typing: `
      bg-whiskers-light border-whiskers-dark text-neutral-900
      shadow-[0_4px_0_0_theme(colors.whiskers.dark)]
      -translate-y-1
    `,
    programming: `
      bg-pixel-light border-pixel-dark text-neutral-900
      shadow-[0_4px_0_0_theme(colors.pixel.dark)]
      -translate-y-1
    `,
  };
  return styles[discipline];
}

export default CountingChallenge;
